import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import TableViewIcon from '@mui/icons-material/TableView';
import CodeIcon from '@mui/icons-material/Code';
import { supabase } from '../utils/supabaseClient';
import { fetchServiceTypes } from '../utils/serviceTypeUtils';
import ConfigurationStatusBadge from '../components/providers/ConfigurationStatusBadge';
import EmailDisplayCell from '../components/providers/EmailDisplayCell';
import { exportSingleProviderToExcel } from '../utils/providerExportUtils';

function ProviderRateDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [activeServiceTypes, setActiveServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    loadProviderDetails();
  }, [id]);

  async function loadProviderDetails() {
    try {
      setLoading(true);
      setError('');
      
      // Fetch active service types
      const { data: serviceTypesData, error: serviceTypesError } = await fetchServiceTypes();
      
      if (serviceTypesError) {
        throw serviceTypesError;
      }
      
      setActiveServiceTypes(serviceTypesData || []);
      
      // Fetch provider with service rate configurations
      const { data: providerData, error: providerError } = await supabase
        .from('providers')
        .select(`
          id,
          name,
          email,
          status,
          net_terms,
          default_hourly_rate,
          default_flat_rate,
          default_refusal_rate,
          provider_service_rates (
            id,
            service_type_id,
            rate_type,
            flat_rate,
            refusal_rate,
            hourly_rate,
            per_board,
            per_location,
            rate_1_unit,
            rate_2_units,
            rate_3_units,
            rate_4_units,
            rate_5_units,
            rate_6_units,
            rate_7_units,
            rate_8_units,
            rate_9_units,
            rate_10_units,
            service_types (
              id,
              name,
              code,
              rate_type,
              flat_completion_rate,
              flat_refusal_rate,
              hourly_rate,
              description
            )
          )
        `)
        .eq('id', id)
        .single();
        
      if (providerError) {
        throw providerError;
      }
      
      if (!providerData) {
        throw new Error('Provider not found');
      }
      
      setProvider(providerData);
    } catch (error) {
      console.error('Error fetching provider details:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return `$${parseFloat(value).toFixed(2)}`;
  };

  const handleBack = () => {
    navigate('/rate-table');
  };

  const handleEdit = () => {
    navigate(`/provider/edit/${id}`);
  };

  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportJSON = () => {
    if (!provider) return;
    
    handleExportMenuClose();
    
    // Create export data
    const exportData = {
      provider: {
        name: provider.name,
        email: provider.email,
        netTerms: provider.net_terms,
        status: provider.status
      },
      serviceRates: (provider.provider_service_rates || []).map(config => ({
        serviceType: config.service_types?.name,
        rateType: config.rate_type,
        ...config
      }))
    };
    
    // Create and download JSON file
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `provider-rates-${provider.name.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportExcel = async () => {
    if (!provider) return;
    
    handleExportMenuClose();
    
    try {
      setExportLoading(true);
      
      // Fetch provider emails
      const { data: authData } = await supabase.auth.getSession();
      const token = authData?.session?.access_token;

      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(`/api/provider-emails/${provider.id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const emailData = await response.json();
      
      const providerWithEmails = {
        ...provider,
        emails: emailData.success ? emailData.emails : []
      };

      // Export to Excel
      const result = exportSingleProviderToExcel(providerWithEmails, activeServiceTypes);
      
      if (!result.success) {
        throw new Error(result.error || 'Export failed');
      }
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      setError(`Export failed: ${error.message}`);
    } finally {
      setExportLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !provider) {
    return (
      <Box sx={{ mt: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || 'Provider not found'}
          </Alert>
          <Button 
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back to Rate Table
          </Button>
        </Paper>
      </Box>
    );
  }

  // Create a map of service type configs for quick lookup
  const configMap = {};
  (provider.provider_service_rates || []).forEach(config => {
    configMap[config.service_type_id] = config;
  });

  const renderServiceTypeCard = (serviceType) => {
    const config = configMap[serviceType.id];
    const isInitialServiceCall = serviceType.name === 'Initial Service Call';
    
    return (
      <Grid item xs={12} md={6} key={serviceType.id}>
        <Card 
          variant="outlined" 
          sx={{ 
            height: '100%',
            backgroundColor: config ? 'inherit' : '#fff3e0',
            border: config ? '1px solid rgba(0, 0, 0, 0.12)' : '2px dashed #ff9800'
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  {serviceType.name}
                </Typography>
                <Chip 
                  label={serviceType.code} 
                  size="small" 
                  sx={{ mr: 1 }}
                />
                {config && (
                  <Chip 
                    label={config.rate_type}
                    size="small"
                    color={config.rate_type === 'FLAT' ? 'primary' : 'secondary'}
                  />
                )}
              </Box>
              {!config && (
                <Chip label="Not Configured" size="small" color="warning" />
              )}
            </Box>

            {config ? (
              <>
                <Divider sx={{ my: 2 }} />
                
                {config.rate_type === 'HOURLY' ? (
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', borderBottom: 'none' }}>Hourly Rate</TableCell>
                          <TableCell align="right" sx={{ borderBottom: 'none' }}>
                            {formatCurrency(config.hourly_rate)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : isInitialServiceCall ? (
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Per Board</TableCell>
                          <TableCell align="right">{formatCurrency(config.per_board)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Per Location</TableCell>
                          <TableCell align="right">{formatCurrency(config.per_location)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>1 Unit</TableCell>
                          <TableCell align="right">{formatCurrency(config.rate_1_unit)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>2 Units</TableCell>
                          <TableCell align="right">{formatCurrency(config.rate_2_units)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>3 Units</TableCell>
                          <TableCell align="right">{formatCurrency(config.rate_3_units)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>4 Units</TableCell>
                          <TableCell align="right">{formatCurrency(config.rate_4_units)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>5 Units</TableCell>
                          <TableCell align="right">{formatCurrency(config.rate_5_units)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>6 Units</TableCell>
                          <TableCell align="right">{formatCurrency(config.rate_6_units)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>7 Units</TableCell>
                          <TableCell align="right">{formatCurrency(config.rate_7_units)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>8 Units</TableCell>
                          <TableCell align="right">{formatCurrency(config.rate_8_units)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>9 Units</TableCell>
                          <TableCell align="right">{formatCurrency(config.rate_9_units)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>10 Units</TableCell>
                          <TableCell align="right">{formatCurrency(config.rate_10_units)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Refusal Rate</TableCell>
                          <TableCell align="right">{formatCurrency(config.refusal_rate)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Completion Rate</TableCell>
                          <TableCell align="right">{formatCurrency(config.flat_rate)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', borderBottom: 'none' }}>Refusal Rate</TableCell>
                          <TableCell align="right" sx={{ borderBottom: 'none' }}>
                            {formatCurrency(config.refusal_rate)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Show comparison with service type defaults */}
                {serviceType.flat_completion_rate || serviceType.hourly_rate ? (
                  <Box sx={{ mt: 2, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Service Type Default:</strong>{' '}
                      {serviceType.rate_type === 'HOURLY' 
                        ? formatCurrency(serviceType.hourly_rate) 
                        : `${formatCurrency(serviceType.flat_completion_rate)} / ${formatCurrency(serviceType.flat_refusal_rate)}`
                      }
                    </Typography>
                  </Box>
                ) : null}
              </>
            ) : (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This service type has not been configured for this provider. Click "Edit Provider" to add rates.
              </Alert>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Button 
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mb: 2 }}
          >
            Back to Rate Table
          </Button>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h4" gutterBottom>
                {provider.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                <EmailDisplayCell providerId={provider.id} />
              </Box>
              <ConfigurationStatusBadge 
                providerServiceRates={provider.provider_service_rates || []}
                activeServiceTypes={activeServiceTypes}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={exportLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
                onClick={handleExportMenuOpen}
                disabled={exportLoading}
              >
                {exportLoading ? 'Exporting...' : 'Export'}
              </Button>
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={handleExportMenuClose}
              >
                <MenuItem onClick={handleExportExcel}>
                  <ListItemIcon>
                    <TableViewIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Export as Excel" />
                </MenuItem>
                <MenuItem onClick={handleExportJSON}>
                  <ListItemIcon>
                    <CodeIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Export as JSON" />
                </MenuItem>
              </Menu>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEdit}
              >
                Edit Provider
              </Button>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Provider Information */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Provider Information
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Status</TableCell>
                      <TableCell>
                        <Chip 
                          label={provider.status || 'Active'} 
                          size="small" 
                          color={provider.status === 'active' ? 'success' : 'default'}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Net Terms</TableCell>
                      <TableCell>{provider.net_terms ? `Net ${provider.net_terms}` : '-'}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Default Rates (Auto-Enrollment)
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Default Flat Rate</TableCell>
                      <TableCell>{formatCurrency(provider.default_flat_rate)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Default Refusal Rate</TableCell>
                      <TableCell>{formatCurrency(provider.default_refusal_rate)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Default Hourly Rate</TableCell>
                      <TableCell>{formatCurrency(provider.default_hourly_rate)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Service Type Configurations */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Service Type Rate Configurations
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Below are the rate configurations for each active service type. Missing configurations are highlighted.
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {activeServiceTypes.length === 0 ? (
            <Grid item xs={12}>
              <Alert severity="info">
                No active service types found in the system.
              </Alert>
            </Grid>
          ) : (
            activeServiceTypes.map(serviceType => renderServiceTypeCard(serviceType))
          )}
        </Grid>
      </Paper>
    </Box>
  );
}

export default ProviderRateDetails;

